import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Clock, Trash2, RotateCcw, RefreshCw, MessageSquare } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const StatusBadge = ({ status, lang }) => {
  const map = {
    pending: { cls: 'bg-amber-500/20 text-amber-700 border-amber-500/40', label: tx(lang, 'قيد الانتظار', 'Pending'), Icon: Clock },
    approved: { cls: 'bg-green-500/20 text-green-700 border-green-500/40', label: tx(lang, 'مقبول', 'Approved'), Icon: CheckCircle2 },
    rejected: { cls: 'bg-red-500/20 text-red-700 border-red-500/40', label: tx(lang, 'مرفوض', 'Rejected'), Icon: XCircle },
  };
  const cur = map[status] || map.pending;
  const I = cur.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-bold ${cur.cls}`}>
      <I className="w-3 h-3" /> {cur.label}
    </span>
  );
};

const RequestsManager = ({ onChanged }) => {
  const { lang, isRTL } = useLang();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending|approved|rejected|all
  const [acting, setActing] = useState(null); // { id, status }
  const [adminNote, setAdminNote] = useState('');
  const L = (ar, en) => tx(lang, ar, en);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await api.get('/admin/requests', { params });
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const resolve = async (req, status) => {
    try {
      await api.patch(`/admin/requests/${req.id}`, { status, admin_note: adminNote });
      toast.success(L(status === 'approved' ? 'تم قبول الطلب' : 'تم رفض الطلب', status === 'approved' ? 'Approved' : 'Rejected'));
      setActing(null); setAdminNote('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const fmt = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString(tx(lang, 'ar-EG', 'en-US')); } catch { return iso; }
  };

  return (
    <div className="space-y-4">
      <div className={`flex items-center gap-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-sm font-bold transition-colors ${filter === f ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}
          >
            {L(
              { pending: 'قيد الانتظار', approved: 'مقبولة', rejected: 'مرفوضة', all: 'الكل' }[f],
              { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', all: 'All' }[f]
            )}
          </button>
        ))}
        <button onClick={load} className="ms-auto px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-slate-900 text-sm flex items-center gap-1.5">
          <RefreshCw className="w-4 h-4" /> {L('تحديث', 'Refresh')}
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-8">{L('جارٍ التحميل...', 'Loading...')}</div>
      ) : requests.length === 0 ? (
        <div className="text-slate-500 text-center py-12 rounded-2xl bg-white border border-slate-200">
          {L('لا توجد طلبات', 'No requests')}
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <div key={r.id} data-testid={`admin-request-${r.id}`} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className={`flex flex-wrap items-center justify-between gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-bold ${r.type === 'delete' ? 'bg-red-500/20 text-red-700' : 'bg-amber-500/20 text-amber-700'}`}>
                    {r.type === 'delete' ? <Trash2 className="w-3 h-3" /> : <RotateCcw className="w-3 h-3" />}
                    {r.type === 'delete' ? L('حذف حساب', 'Account Delete') : L('استرداد', 'Refund')}
                  </span>
                  <StatusBadge status={r.status} lang={lang} />
                </div>
                <div className="text-xs text-slate-500">{fmt(r.created_at)}</div>
              </div>

              <div className={`text-sm mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                <span className="text-slate-500">{L('المستخدم:', 'User:')}</span>{' '}
                <span className="text-slate-900 font-bold">{r.user_name}</span>{' '}
                <span className="text-slate-500" dir="ltr">({r.user_email})</span>
              </div>

              {r.reason && (
                <div className={`p-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 text-sm mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {L('السبب', 'Reason')}</div>
                  {r.reason}
                </div>
              )}

              {r.admin_note && (
                <div className={`p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-sm mb-3 italic ${isRTL ? 'text-right' : 'text-left'}`}>
                  {L('رد الإدارة:', 'Admin reply:')} {r.admin_note}
                </div>
              )}

              {r.status === 'pending' && (
                acting?.id === r.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder={L('ملاحظة للمستخدم (اختياري)', 'Note to user (optional)')}
                      rows={2}
                      className="bg-slate-50 border-slate-200 text-slate-900"
                      data-testid={`admin-note-${r.id}`}
                    />
                    <div className="flex gap-2">
                      <Button onClick={() => resolve(r, acting.status)} className={`border-0 ${acting.status === 'approved' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`} data-testid={`confirm-${acting.status}-${r.id}`}>
                        {acting.status === 'approved' ? L('تأكيد القبول', 'Confirm Approve') : L('تأكيد الرفض', 'Confirm Reject')}
                      </Button>
                      <Button onClick={() => { setActing(null); setAdminNote(''); }} variant="outline" className="border-blue-200 text-slate-700">
                        {L('إلغاء', 'Cancel')}
                      </Button>
                    </div>
                    {acting.status === 'approved' && r.type === 'delete' && (
                      <div className="text-red-700 text-xs font-bold">⚠️ {L('سيتم حذف هذا المستخدم وكل بياناته نهائياً', 'This will permanently delete the user and all their data')}</div>
                    )}
                    {acting.status === 'approved' && r.type === 'refund' && (
                      <div className="text-amber-700 text-xs font-bold">⚠️ {L('سيتم إلغاء اشتراك هذا المستخدم', 'This will cancel the user subscription')}</div>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={() => { setActing({ id: r.id, status: 'approved' }); setAdminNote(''); }} className="bg-green-600 hover:bg-green-700 text-white border-0" data-testid={`approve-${r.id}`}>
                      <CheckCircle2 className="w-4 h-4 me-2" /> {L('قبول', 'Approve')}
                    </Button>
                    <Button onClick={() => { setActing({ id: r.id, status: 'rejected' }); setAdminNote(''); }} className="bg-red-600 hover:bg-red-700 text-white border-0" data-testid={`reject-${r.id}`}>
                      <XCircle className="w-4 h-4 me-2" /> {L('رفض', 'Reject')}
                    </Button>
                  </div>
                )
              )}
              {r.resolved_at && r.status !== 'pending' && (
                <div className="text-xs text-slate-500 mt-2">{L('تمت المعالجة:', 'Resolved:')} {fmt(r.resolved_at)}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequestsManager;
